// apps/web/src/pages/Admin/AdminQuizQuestions.js
import React, { useEffect, useState, useCallback, useRef } from 'react';
import adminApi from '../../services/adminApi';
import styles from './AdminQuizQuestions.module.css';

const KIND_OPTIONS = ['movie', 'tv'];

const EMPTY_CREATE_FORM = {
    tmdb_id: '',
    kind: 'movie',
    season_number: '',
    question: '',
    options: ['', '', '', ''],
    correct_index: 0,
    active: true,
};

function rateClass(rate) {
    if (rate >= 70) return styles.statRateGood;
    if (rate >= 40) return styles.statRateMid;
    return styles.statRateLow;
}

// ── Movie/Show typeahead ─────────────────────────────────────────────────────
function MediaTypeahead({ onPick }) {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [open, setOpen] = useState(false);
    const debounceRef = useRef(null);

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (!query.trim()) { setResults([]); return; }
        debounceRef.current = setTimeout(async () => {
            try {
                const data = await adminApi.searchMedia({ search: query, limit: 8 });
                setResults(data.media);
                setOpen(true);
            } catch (err) {
                console.error('Media search failed:', err);
            }
        }, 300);
        return () => clearTimeout(debounceRef.current);
    }, [query]);

    return (
        <div className={styles.typeaheadWrap}>
            <label className={styles.label}>Find Existing Movie/Show</label>
            <input
                className={styles.input}
                type="text"
                placeholder="Search by title…"
                value={query}
                onChange={e => setQuery(e.target.value)}
                onFocus={() => results.length && setOpen(true)}
                onBlur={() => setTimeout(() => setOpen(false), 150)}
                style={{ width: '100%' }}
            />
            {open && results.length > 0 && (
                <div className={styles.typeaheadResults}>
                    {results.map(m => (
                        <div
                            key={m.id}
                            className={styles.typeaheadItem}
                            onMouseDown={() => {
                                onPick(m);
                                setQuery(m.title);
                                setOpen(false);
                            }}
                        >
                            {m.poster_url ? (
                                <img src={m.poster_url} alt="" className={styles.typeaheadPoster} onError={e => { e.currentTarget.style.display = 'none'; }} />
                            ) : (
                                <div className={styles.typeaheadPosterPlaceholder}>{m.kind === 'movie' ? '🎬' : '📺'}</div>
                            )}
                            <span>{m.title}</span>
                            <span className={styles.typeaheadMeta}>{m.year ?? ''} · {m.questionCount} question{m.questionCount === 1 ? '' : 's'}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ── Question Detail / Edit Panel ─────────────────────────────────────────────
function QuestionDetailPanel({ questionId, onClose, onQuestionUpdated }) {
    const [detail, setDetail] = useState(null);
    const [reports, setReports] = useState([]);
    const [loadingDetail, setLoading] = useState(true);
    const [editForm, setEditForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [panelError, setPanelError] = useState(null);
    const [panelSuccess, setPanelSuccess] = useState(null);

    const fetchDetail = useCallback(async () => {
        try {
            setLoading(true);
            const data = await adminApi.getQuizQuestion(questionId);
            setDetail(data.question);
            setReports(data.reports);
            setEditForm({
                question: data.question.question,
                options: [...data.question.options_json],
                correct_index: data.question.correct_index,
                season_number: data.question.season_number ?? '',
                active: data.question.active,
            });
        } catch (err) {
            console.error('Failed to load question detail:', err);
        } finally {
            setLoading(false);
        }
    }, [questionId]);

    useEffect(() => { fetchDetail(); }, [fetchDetail]);

    function setField(key, value) {
        setEditForm(prev => ({ ...prev, [key]: value }));
    }

    function setOption(index, value) {
        setEditForm(prev => {
            const options = [...prev.options];
            options[index] = value;
            return { ...prev, options };
        });
    }

    function addOption() {
        setEditForm(prev => ({ ...prev, options: [...prev.options, ''] }));
    }

    function removeOption(index) {
        setEditForm(prev => {
            const options = prev.options.filter((_, i) => i !== index);
            let correct_index = prev.correct_index;
            if (index === prev.correct_index) correct_index = 0;
            else if (index < prev.correct_index) correct_index -= 1;
            return { ...prev, options, correct_index };
        });
    }

    async function handleSave(e) {
        e.preventDefault();
        setPanelError(null);
        setPanelSuccess(null);
        setSaving(true);
        try {
            await adminApi.updateQuizQuestion(questionId, {
                question: editForm.question,
                options: editForm.options,
                correct_index: editForm.correct_index,
                season_number: detail.media?.kind === 'tv'
                    ? (editForm.season_number !== '' ? parseInt(editForm.season_number, 10) : null)
                    : null,
                active: editForm.active,
            });
            setPanelSuccess('Question saved!');
            await fetchDetail();
            onQuestionUpdated?.();
        } catch (err) {
            setPanelError(err.response?.data?.message || err.message || 'Failed to save question.');
        } finally {
            setSaving(false);
        }
    }

    const media = detail?.media;
    const stats = detail?.stats;

    return (
        <div className={styles.detailOverlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div className={styles.detailPanel}>
                <div className={styles.detailHeader}>
                    <button className={styles.detailCloseBtn} onClick={onClose}>✕</button>
                    {media ? (
                        <>
                            {media.poster_url ? (
                                <img src={media.poster_url} alt="" className={styles.detailPoster} onError={e => { e.currentTarget.style.display = 'none'; }} />
                            ) : (
                                <div className={styles.detailPosterPlaceholder}>{media.kind === 'movie' ? '🎬' : '📺'}</div>
                            )}
                            <div>
                                <h2 className={styles.detailTitle}>{media.title}</h2>
                                <div className={styles.detailMeta}>
                                    <span className={styles.kindPill}>{media.kind === 'movie' ? '🎬 Movie' : '📺 TV'}</span>
                                    {detail.season_number != null && (
                                        <span className={styles.seasonPill}>Season {detail.season_number}</span>
                                    )}
                                    <span className={styles.tmdbId}>TMDB {media.tmdb_id}</span>
                                </div>
                            </div>
                        </>
                    ) : <h2 className={styles.detailTitle}>Loading…</h2>}
                </div>

                {loadingDetail ? (
                    <div className={styles.loading}>Loading question…</div>
                ) : (
                    <div className={styles.detailBody}>
                        {/* ── Stats ── */}
                        <div className={styles.detailSection}>
                            <h3 className={styles.detailSectionTitle}>Answer Stats</h3>
                            <div className={styles.statsGrid}>
                                <div className={styles.statTile}>
                                    <div className={styles.statTileValue}>{stats?.correct ?? 0}</div>
                                    <div className={styles.statTileLabel}>Correct</div>
                                </div>
                                <div className={styles.statTile}>
                                    <div className={styles.statTileValue}>{stats?.incorrect ?? 0}</div>
                                    <div className={styles.statTileLabel}>Incorrect</div>
                                </div>
                                <div className={styles.statTile}>
                                    <div className={`${styles.statTileValue} ${rateClass(stats?.correctRate ?? 0)}`}>{stats?.correctRate ?? 0}%</div>
                                    <div className={styles.statTileLabel}>Correct Rate</div>
                                </div>
                            </div>
                        </div>

                        {/* ── Edit form ── */}
                        <div className={styles.detailSection}>
                            <h3 className={styles.detailSectionTitle}>Edit Question</h3>
                            {panelError && <div className={styles.errorBanner}>{panelError}</div>}
                            {panelSuccess && <div className={styles.successBanner}>{panelSuccess}</div>}

                            {editForm && (
                                <form onSubmit={handleSave}>
                                    <div className={styles.fieldGroupFull} style={{ marginBottom: 14 }}>
                                        <label className={styles.label}>Question</label>
                                        <textarea
                                            className={styles.textarea}
                                            rows={3}
                                            value={editForm.question}
                                            onChange={e => setField('question', e.target.value)}
                                            required
                                        />
                                    </div>

                                    {media?.kind === 'tv' && (
                                        <div className={styles.fieldGroup} style={{ marginBottom: 14, maxWidth: 200 }}>
                                            <label className={styles.label}>Season # (optional)</label>
                                            <input
                                                className={styles.input}
                                                type="number"
                                                min="1"
                                                placeholder="All seasons"
                                                value={editForm.season_number}
                                                onChange={e => setField('season_number', e.target.value)}
                                            />
                                        </div>
                                    )}

                                    <div className={styles.optionsSection}>
                                        <div className={styles.optionsHeader}>
                                            <span className={styles.label}>Options (select the correct one)</span>
                                            <button type="button" className={styles.addItemBtn} onClick={addOption}>+ Add Option</button>
                                        </div>
                                        {editForm.options.map((opt, idx) => (
                                            <div key={idx} className={styles.optionRow}>
                                                <input
                                                    type="radio"
                                                    className={styles.optionRadio}
                                                    name="correct_index"
                                                    checked={editForm.correct_index === idx}
                                                    onChange={() => setField('correct_index', idx)}
                                                    title="Mark as correct answer"
                                                />
                                                <input
                                                    className={`${styles.input} ${styles.optionInput}`}
                                                    type="text"
                                                    value={opt}
                                                    onChange={e => setOption(idx, e.target.value)}
                                                    required
                                                />
                                                <button
                                                    type="button"
                                                    className={styles.removeItemBtn}
                                                    disabled={editForm.options.length <= 2}
                                                    onClick={() => removeOption(idx)}
                                                    title="Remove option"
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        ))}
                                    </div>

                                    <label className={styles.toggleLabel} style={{ marginBottom: 16 }}>
                                        <input
                                            type="checkbox"
                                            className={styles.checkbox}
                                            checked={editForm.active}
                                            onChange={e => setField('active', e.target.checked)}
                                        />
                                        <span>{editForm.active ? 'Active (shown in quizzes)' : 'Inactive (hidden)'}</span>
                                    </label>

                                    <button type="submit" className={styles.addItemSubmitBtn} disabled={saving}>
                                        {saving ? 'Saving…' : '💾 Save Question'}
                                    </button>
                                </form>
                            )}
                        </div>

                        {/* ── Reports ── */}
                        <div className={styles.detailSection}>
                            <h3 className={styles.detailSectionTitle}>User Reports<span className={styles.itemCount}>{reports.length}</span></h3>
                            {reports.length === 0 ? (
                                <div className={styles.detailEmpty}>No reports for this question.</div>
                            ) : (
                                reports.map(r => (
                                    <div key={r.id} className={styles.reportRow}>
                                        <span className={styles.reportReason}>{r.reason.replace(/_/g, ' ')}</span>
                                        <span className={styles.reportMeta}>
                                            {r.username || 'Anonymous'} · {r.status} · {new Date(r.createdAt).toLocaleDateString()}
                                        </span>
                                        {r.message && <span className={styles.reportMessage}>{r.message}</span>}
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// ── Main Page ────────────────────────────────────────────────────────────────
export default function AdminQuizQuestions() {
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(EMPTY_CREATE_FORM);
    const [submitting, setSubmitting] = useState(false);
    const [toggling, setToggling] = useState(null);
    const [error, setError] = useState(null);
    const [successMsg, setSuccessMsg] = useState(null);
    const [search, setSearch] = useState('');
    const [kindFilter, setKindFilter] = useState('all');
    const [activeFilter, setActiveFilter] = useState('all');
    const [pagination, setPagination] = useState(null);
    const [page, setPage] = useState(1);
    const [selectedQuestionId, setSelectedQuestionId] = useState(null);

    const fetchQuestions = useCallback(async () => {
        try {
            setLoading(true);
            const data = await adminApi.listQuizQuestions({
                page,
                limit: 20,
                search,
                kind: kindFilter !== 'all' ? kindFilter : undefined,
                active: activeFilter !== 'all' ? (activeFilter === 'active') : undefined,
            });
            setQuestions(data.questions);
            setPagination(data.pagination);
        } catch (err) {
            console.error('Failed to load quiz questions:', err);
        } finally {
            setLoading(false);
        }
    }, [page, search, kindFilter, activeFilter]);

    useEffect(() => { fetchQuestions(); }, [fetchQuestions]);

    function setField(key, value) {
        setForm(prev => ({ ...prev, [key]: value }));
    }

    function setOption(index, value) {
        setForm(prev => {
            const options = [...prev.options];
            options[index] = value;
            return { ...prev, options };
        });
    }

    function addOption() {
        setForm(prev => ({ ...prev, options: [...prev.options, ''] }));
    }

    function removeOption(index) {
        setForm(prev => {
            const options = prev.options.filter((_, i) => i !== index);
            let correct_index = prev.correct_index;
            if (index === prev.correct_index) correct_index = 0;
            else if (index < prev.correct_index) correct_index -= 1;
            return { ...prev, options, correct_index };
        });
    }

    function handlePickMedia(media) {
        setForm(prev => ({ ...prev, tmdb_id: String(media.tmdb_id), kind: media.kind }));
    }

    async function handleSubmit(e) {
        e.preventDefault();
        setError(null);
        setSuccessMsg(null);
        setSubmitting(true);
        try {
            const payload = {
                tmdb_id: parseInt(form.tmdb_id, 10),
                kind: form.kind,
                season_number: form.kind === 'tv' && form.season_number !== '' ? parseInt(form.season_number, 10) : null,
                question: form.question,
                options: form.options,
                correct_index: form.correct_index,
                active: form.active,
            };
            await adminApi.createQuizQuestion(payload);
            setSuccessMsg('Question created successfully!');
            setForm(EMPTY_CREATE_FORM);
            setShowForm(false);
            fetchQuestions();
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Failed to create question.');
        } finally {
            setSubmitting(false);
        }
    }

    async function handleToggleActive(q) {
        setToggling(q.id);
        try {
            await adminApi.toggleQuizQuestionActive(q.id, !q.active);
            setQuestions(prev => prev.map(x => x.id === q.id ? { ...x, active: !x.active } : x));
        } catch (err) {
            console.error('Failed to toggle question:', err);
        } finally {
            setToggling(null);
        }
    }

    return (
        <div className={styles.page}>
            {selectedQuestionId && (
                <QuestionDetailPanel
                    questionId={selectedQuestionId}
                    onClose={() => setSelectedQuestionId(null)}
                    onQuestionUpdated={fetchQuestions}
                />
            )}

            <div className={styles.header}>
                <h1 className={styles.pageTitle}>❓ Quiz Questions</h1>
                <button
                    className={styles.createBtn}
                    onClick={() => { setShowForm(v => !v); setError(null); setSuccessMsg(null); }}
                >
                    {showForm ? '✕ Cancel' : '+ New Question'}
                </button>
            </div>

            {successMsg && <div className={styles.successBanner}>{successMsg}</div>}

            {showForm && (
                <form className={styles.form} onSubmit={handleSubmit}>
                    <h2 className={styles.formTitle}>Create Question</h2>
                    {error && <div className={styles.errorBanner}>{error}</div>}

                    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 8 }}>
                        <MediaTypeahead onPick={handlePickMedia} />
                    </div>
                    <div className={styles.orDivider}>— or specify a TMDB id directly (creates the movie/show if it doesn't exist yet) —</div>

                    <div className={styles.formGrid}>
                        <div className={styles.fieldGroup}>
                            <label className={styles.label}>TMDB ID *</label>
                            <input
                                className={styles.input}
                                type="number"
                                min="1"
                                placeholder="e.g. 1726"
                                value={form.tmdb_id}
                                onChange={e => setField('tmdb_id', e.target.value)}
                                required
                            />
                        </div>

                        <div className={styles.fieldGroup}>
                            <label className={styles.label}>Kind *</label>
                            <select
                                className={styles.select}
                                value={form.kind}
                                onChange={e => setField('kind', e.target.value)}
                            >
                                {KIND_OPTIONS.map(k => (
                                    <option key={k} value={k}>{k === 'movie' ? '🎬 Movie' : '📺 TV Series'}</option>
                                ))}
                            </select>
                        </div>

                        {form.kind === 'tv' && (
                            <div className={styles.fieldGroup}>
                                <label className={styles.label}>Season # (optional)</label>
                                <input
                                    className={styles.input}
                                    type="number"
                                    min="1"
                                    placeholder="All seasons"
                                    value={form.season_number}
                                    onChange={e => setField('season_number', e.target.value)}
                                />
                            </div>
                        )}

                        <div className={styles.fieldGroup}>
                            <label className={styles.label}>Active</label>
                            <label className={styles.toggleLabel}>
                                <input
                                    type="checkbox"
                                    checked={form.active}
                                    onChange={e => setField('active', e.target.checked)}
                                    className={styles.checkbox}
                                />
                                <span>{form.active ? 'Active' : 'Inactive'}</span>
                            </label>
                        </div>
                    </div>

                    <div className={styles.fieldGroupFull} style={{ marginBottom: 20 }}>
                        <label className={styles.label}>Question *</label>
                        <textarea
                            className={styles.textarea}
                            rows={3}
                            placeholder="e.g. Who directed this movie?"
                            value={form.question}
                            onChange={e => setField('question', e.target.value)}
                            required
                        />
                    </div>

                    <div className={styles.optionsSection}>
                        <div className={styles.optionsHeader}>
                            <h3 className={styles.optionsTitle}>Answer Options</h3>
                            <button type="button" className={styles.addItemBtn} onClick={addOption}>+ Add Option</button>
                        </div>
                        <p className={styles.optionsHint}>Select the radio button next to the correct answer.</p>

                        {form.options.map((opt, idx) => (
                            <div key={idx} className={styles.optionRow}>
                                <input
                                    type="radio"
                                    className={styles.optionRadio}
                                    name="new_correct_index"
                                    checked={form.correct_index === idx}
                                    onChange={() => setField('correct_index', idx)}
                                    title="Mark as correct answer"
                                />
                                <input
                                    className={`${styles.input} ${styles.optionInput}`}
                                    type="text"
                                    placeholder={`Option ${idx + 1}`}
                                    value={opt}
                                    onChange={e => setOption(idx, e.target.value)}
                                    required
                                />
                                <button
                                    type="button"
                                    className={styles.removeItemBtn}
                                    disabled={form.options.length <= 2}
                                    onClick={() => removeOption(idx)}
                                    title="Remove option"
                                >
                                    ✕
                                </button>
                            </div>
                        ))}
                    </div>

                    <div className={styles.formActions}>
                        <button type="submit" className={styles.submitBtn} disabled={submitting}>
                            {submitting ? 'Creating…' : '✓ Create Question'}
                        </button>
                        <button
                            type="button"
                            className={styles.cancelBtn}
                            onClick={() => { setShowForm(false); setForm(EMPTY_CREATE_FORM); setError(null); }}
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            )}

            <div className={styles.searchRow}>
                <input
                    className={styles.searchInput}
                    type="text"
                    placeholder="Search by movie/show title or question text…"
                    value={search}
                    onChange={e => { setSearch(e.target.value); setPage(1); }}
                />
                <div className={styles.filterPills}>
                    {['all', 'movie', 'tv'].map(f => (
                        <button
                            key={f}
                            className={`${styles.filterPill} ${kindFilter === f ? styles.filterPillActive : ''}`}
                            onClick={() => { setKindFilter(f); setPage(1); }}
                        >
                            {f === 'all' ? 'All' : f === 'movie' ? '🎬 Movies' : '📺 TV'}
                        </button>
                    ))}
                </div>
                <div className={styles.filterPills}>
                    {['all', 'active', 'inactive'].map(f => (
                        <button
                            key={f}
                            className={`${styles.filterPill} ${activeFilter === f ? styles.filterPillActive : ''}`}
                            onClick={() => { setActiveFilter(f); setPage(1); }}
                        >
                            {f.charAt(0).toUpperCase() + f.slice(1)}
                        </button>
                    ))}
                </div>
                {pagination && (
                    <span className={styles.totalCount}>{pagination.total} total</span>
                )}
            </div>

            {loading ? (
                <div className={styles.loading}>Loading questions…</div>
            ) : questions.length === 0 ? (
                <div className={styles.empty}>
                    <div className={styles.emptyIcon}>❓</div>
                    <div>No questions found. Create one above.</div>
                </div>
            ) : (
                <>
                    <div className={styles.table}>
                        <table>
                            <thead>
                            <tr>
                                <th>Movie / Show</th>
                                <th>Question</th>
                                <th>Correct Rate</th>
                                <th>Status</th>
                                <th>Created</th>
                                <th>Active</th>
                            </tr>
                            </thead>
                            <tbody>
                            {questions.map(q => (
                                <tr
                                    key={q.id}
                                    className={styles.clickableRow}
                                    onClick={() => setSelectedQuestionId(q.id)}
                                >
                                    <td>
                                        <div className={styles.mediaCell}>
                                            {q.media?.poster_url ? (
                                                <img src={q.media.poster_url} alt="" className={styles.posterThumb} onError={e => { e.currentTarget.style.display = 'none'; }} />
                                            ) : (
                                                <div className={styles.posterThumbPlaceholder}>{q.media?.kind === 'movie' ? '🎬' : '📺'}</div>
                                            )}
                                            <div>
                                                <div className={styles.mediaTitle}>{q.media?.title ?? 'Unknown'}</div>
                                                <div className={styles.mediaMeta}>
                                                    {q.media?.kind === 'tv' ? 'TV' : 'Movie'}
                                                    {q.season_number != null ? ` · Season ${q.season_number}` : ''}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className={styles.questionCell} title={q.question}>{q.question}</td>
                                    <td>
                                        <div className={styles.statCell}>
                                            <span className={`${styles.statRate} ${rateClass(q.stats?.correctRate ?? 0)}`}>{q.stats?.correctRate ?? 0}%</span>
                                            <span className={styles.statCount}>{q.stats?.total ?? 0} answered</span>
                                        </div>
                                    </td>
                                    <td>
                                        <span className={q.active ? styles.statusActive : styles.statusInactive}>
                                            {q.active ? '● Active' : '○ Inactive'}
                                        </span>
                                    </td>
                                    <td className={styles.date}>
                                        {q.createdAt ? new Date(q.createdAt).toLocaleDateString() : '—'}
                                    </td>
                                    <td onClick={e => e.stopPropagation()}>
                                        <button
                                            className={q.active ? styles.toggleBtnOn : styles.toggleBtnOff}
                                            disabled={toggling === q.id}
                                            onClick={() => handleToggleActive(q)}
                                            title={q.active ? 'Deactivate question' : 'Activate question'}
                                        >
                                            {toggling === q.id ? '…' : q.active ? 'Deactivate' : 'Activate'}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>

                    {pagination && pagination.totalPages > 1 && (
                        <div className={styles.pagination}>
                            <button
                                disabled={page === 1}
                                onClick={() => setPage(p => p - 1)}
                                className={styles.pageBtn}
                            >
                                Previous
                            </button>
                            <span className={styles.pageInfo}>Page {page} of {pagination.totalPages}</span>
                            <button
                                disabled={page === pagination.totalPages}
                                onClick={() => setPage(p => p + 1)}
                                className={styles.pageBtn}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
